   // Web Serial API helper for reading live weight from a digital weighing scale
// indicator (RS232/USB-serial). Most indicators stream ASCII lines such as
// "ST,GS,+001.250kg\r\n" whenever the reading is stable. Only supported in
// Chromium-based browsers (Chrome/Edge) served over a secure context.

export const isSerialSupported = () =>
  typeof navigator !== "undefined" &&
  "serial" in navigator &&
  // Web Serial only works in a secure context (HTTPS or localhost).
  (typeof window === "undefined" || window.isSecureContext !== false);

// Ports the user has already granted access to in a previous session, so we
// can offer them directly instead of always showing the native chooser.
export async function listPairedPorts() {
  if (!isSerialSupported()) return [];
  return navigator.serial.getPorts();
}

// Human readable label for a SerialPort, used in the device picker menu.
export function describePort(port, index = 0) {
  const info = port.getInfo?.() || {};
  if (info.usbVendorId != null || info.usbProductId != null) {
    const vid = info.usbVendorId?.toString(16).padStart(4, "0") || "????";
    const pid = info.usbProductId?.toString(16).padStart(4, "0") || "????";
    return `USB Device (VID:${vid} PID:${pid})`;
  }
  return `Serial Port ${index + 1}`;
}

// Opens the browser's native "select a device to pair" dialog and returns the
// chosen SerialPort. Must be called directly from a user gesture (e.g. click).
export async function requestNewPort() {
  if (!isSerialSupported()) {
    throw new Error(
      "Web Serial API is not supported here. Use Chrome/Edge over HTTPS (or localhost), or enter weight manually.",
    );
  }
  return navigator.serial.requestPort();
}

// Extracts the first signed decimal number from a raw line sent by the scale
// and normalizes it to kilograms (treats a bare "g" unit, without "kg", as grams).
export function parseWeightFromLine(line) {
  if (!line) return null;
  const match = line.match(/([+-]?\d+(?:\.\d+)?)/);
  if (!match) return null;
  const value = parseFloat(match[1]);
  if (Number.isNaN(value)) return null;
  const isGrams = /\bg\b/i.test(line) && !/kg/i.test(line);
  return isGrams ? value / 1000 : value;
}

// Opens the given serial port (or prompts the user to pick one if none is
// passed) and streams decoded lines. onWeight(weightKg) fires for every line
// a numeric weight can be parsed from. Returns { disconnect, port }.
export async function connectScale({
  port,
  baudRate = 9600,
  onWeight,
  onRawLine,
  onError,
} = {}) {
  if (!isSerialSupported()) {
    throw new Error(
      "Web Serial API is not supported here. Use Chrome/Edge over HTTPS (or localhost), or enter weight manually.",
    );
  }

  const portRef = port || (await navigator.serial.requestPort());
  await portRef.open({ baudRate });

  const textDecoder = new TextDecoderStream();
  const readableClosed = portRef.readable.pipeTo(textDecoder.writable);
  const reader = textDecoder.readable.getReader();

  let buffer = "";
  let stopped = false;

  (async function readLoop() {
    try {
      while (!stopped) {
        const { value, done } = await reader.read();
        if (done) break;
        if (!value) continue;
        buffer += value;
        let idx;
        while ((idx = buffer.search(/[\r\n]/)) >= 0) {
          const line = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 1);
          if (line) {
            onRawLine?.(line);
            const weightKg = parseWeightFromLine(line);
            if (weightKg !== null) onWeight?.(weightKg);
          }
        }
      }
    } catch (err) {
      if (!stopped) onError?.(err);
    }
  })();

  return {
    port: portRef,
    disconnect: async () => {
      stopped = true;
      try {
        await reader.cancel();
      } catch (e) {
        /* ignore */
      }
      try {
        reader.releaseLock();
      } catch (e) {
        /* ignore */
      }
      try {
        await readableClosed.catch(() => {});
      } catch (e) {
        /* ignore */
      }
      try {
        await portRef.close();
      } catch (e) {
        /* ignore */
      }
    },
  };
}

// Formats a kilogram value the way it should appear on a printed sticker,
// e.g. 0.2 -> "200 g", 1.5 -> "1.500 kg".
export function formatWeight(weightKg) {
  const value = Number(weightKg) || 0;
  if (value < 1) {
    return `${Math.round(value * 1000)} g`;
  }
  return `${value.toFixed(3)} kg`;
}
